import { getCountries, getCountryCallingCode, type CountryCode } from 'libphonenumber-js/min';
import copy from '../copy/en/intake.json' with { type: 'json' };
import { createFieldPopup } from './field-popup.js';

const normalize = (value: string) => value.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim();
const names = new Intl.DisplayNames(['en'], { type: 'region' });
const countries = getCountries().map(code => ({ code, name: names.of(code) ?? code, dial: `+${getCountryCallingCode(code)}` }))
  .sort((a, b) => a.name.localeCompare(b.name, 'en'));
let nextId = 0;

export function createCountryPicker(options: { label: string; onChange(country: CountryCode): void }) {
  const id = `phone-country-${++nextId}`;
  const button = document.createElement('button');
  button.type = 'button'; button.className = 'phone-country';
  button.setAttribute('aria-haspopup', 'dialog'); button.setAttribute('aria-controls', id);
  button.setAttribute('aria-expanded', 'false');
  const display = document.createElement('span'); display.className = 'phone-country-display';
  const chevron = document.createElement('span'); chevron.className = 'phone-country-chevron'; chevron.setAttribute('aria-hidden', 'true');
  button.append(display, chevron);
  const panel = document.createElement('div'); panel.id = id; panel.className = 'country-picker';
  panel.setAttribute('role', 'dialog'); panel.setAttribute('aria-label', options.label);
  const search = document.createElement('input'); search.type = 'search'; search.className = 'field-popup-search';
  search.placeholder = copy.searchPhoneCountries; search.setAttribute('aria-label', copy.searchPhoneCountries);
  search.autocomplete = 'off'; search.spellcheck = false;
  search.setAttribute('role', 'combobox'); search.setAttribute('aria-autocomplete', 'list'); search.setAttribute('aria-expanded', 'false');
  const list = document.createElement('div'); list.id = `${id}-list`; list.className = 'field-popup-list';
  list.setAttribute('role', 'listbox'); list.setAttribute('aria-label', copy.phoneCountries);
  search.setAttribute('aria-controls', list.id);
  const empty = document.createElement('p'); empty.className = 'field-popup-empty'; empty.setAttribute('role', 'status'); empty.textContent = copy.noPhoneCountries;
  panel.append(search, list, empty);
  let selected: CountryCode | undefined = 'US', active = -1, shown = countries, open = false;
  const close = (restore = false) => {
    open = false; popup.hide(); button.setAttribute('aria-expanded', 'false'); search.setAttribute('aria-expanded', 'false');
    search.removeAttribute('aria-activedescendant');
    if (restore && button.isConnected && !button.matches(':disabled')) button.focus();
  };
  const popup = createFieldPopup(button, panel, { minWidth: 280, maxWidth: 330, onDismiss: () => close() });
  const setActive = (index: number) => {
    active = index;
    for (const [i, row] of Array.from(list.children).entries()) row.setAttribute('data-active', String(i === active));
    const row = list.children[active];
    if (row) { search.setAttribute('aria-activedescendant', row.id); row.scrollIntoView?.({ block: 'nearest' }); }
    else search.removeAttribute('aria-activedescendant');
  };
  const choose = (index: number) => {
    const country = shown[index];
    if (!country || button.matches(':disabled')) return;
    options.onChange(country.code); close(true);
  };
  const render = () => {
    const terms = normalize(search.value).split(/\s+/).filter(Boolean);
    shown = countries.filter(country => terms.every(term => normalize(`${country.name} ${country.code} ${country.dial}`).includes(term)));
    list.replaceChildren(...shown.map((country, index) => {
      const row = document.createElement('button'); row.type = 'button'; row.className = 'field-popup-option country-option';
      row.id = `${id}-${country.code}`; row.dataset.country = country.code; row.tabIndex = -1;
      row.setAttribute('role', 'option'); row.setAttribute('aria-selected', String(country.code === selected));
      const name = document.createElement('span'); name.className = 'country-option-name'; name.textContent = country.name;
      const code = document.createElement('small'); code.textContent = country.code; name.append(code);
      const dial = document.createElement('span'); dial.className = 'country-option-dial'; dial.textContent = country.dial;
      const check = document.createElement('span'); check.className = 'country-option-check'; check.setAttribute('aria-hidden', 'true'); check.textContent = country.code === selected ? '✓' : '';
      row.append(name, dial, check);
      row.addEventListener('pointerdown', event => event.preventDefault());
      row.addEventListener('click', () => choose(index));
      return row;
    }));
    empty.hidden = shown.length > 0;
    setActive(terms.length ? (shown.length ? 0 : -1) : shown.findIndex(country => country.code === selected));
    popup.reposition();
  };
  const show = () => {
    if (button.matches(':disabled')) return;
    open = true; search.value = ''; render(); popup.show();
    button.setAttribute('aria-expanded', 'true'); search.setAttribute('aria-expanded', 'true'); search.focus();
    setActive(active);
  };
  const toggle = () => { if (open) close(true); else show(); };
  const onTriggerKey = (event: KeyboardEvent) => { if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); show(); } };
  const onSearchKey = (event: KeyboardEvent) => {
    if (event.isComposing) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault(); event.stopPropagation();
      if (shown.length) setActive(active < 0
        ? (event.key === 'ArrowDown' ? 0 : shown.length - 1)
        : (active + (event.key === 'ArrowDown' ? 1 : -1) + shown.length) % shown.length);
    } else if (event.key === 'Enter') { event.preventDefault(); event.stopPropagation(); choose(active); }
    else if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(true); }
    else if (event.key === 'Tab') { close(true); }
  };
  button.addEventListener('click', toggle); button.addEventListener('keydown', onTriggerKey);
  search.addEventListener('input', render); search.addEventListener('keydown', onSearchKey);
  return {
    element: button,
    setCountry(country: CountryCode | undefined, fallback = '+') {
      selected = country;
      const value = countries.find(option => option.code === country);
      display.textContent = value ? `${value.code} ${value.dial}` : fallback;
      button.dataset.country = country ?? '';
      button.setAttribute('aria-label', `${options.label}: ${value ? `${value.name} (${value.code}) ${value.dial}` : fallback}`);
      button.title = value?.name ?? fallback;
      if (open) render();
    },
    dispose() { close(); popup.dispose(); button.removeEventListener('click', toggle); button.removeEventListener('keydown', onTriggerKey); search.removeEventListener('input', render); search.removeEventListener('keydown', onSearchKey); },
  };
}
